export type DiscordActivity = { type: 0 | 1 | 2 | 3 | 4 | 5; name: string; details?: string; state?: string; imageUrl?: string }
export type DiscordPresence = { state: 'loading' | 'unavailable' | 'not-configured' | 'online' | 'idle' | 'dnd' | 'offline'; activities?: DiscordActivity[] }

const userId = import.meta.env.VITE_DISCORD_USER_ID
const text = (value: unknown, limit = 120) => typeof value === 'string' && value.trim() ? value.trim().slice(0, limit) : undefined
const token = (value: unknown, limit: number) => typeof value === 'string' && value.trim() && value.trim().length <= limit ? value.trim() : undefined
const activityTypes = new Set([0, 1, 2, 3, 4, 5])
const spotifyAlbumArt = (value: unknown) => {
  const candidate = token(value, 1_024)
  if (!candidate) return undefined
  try {
    const url = new URL(candidate)
    return url.protocol === 'https:' && url.hostname === 'i.scdn.co' && !url.username && !url.password ? url.href : undefined
  } catch { return undefined }
}
const assetUrl = (asset: unknown, applicationId: unknown) => {
  const value = token(asset, 512)
  if (!value) return undefined
  if (value.startsWith('mp:')) {
    const id = value.slice(3)
    if (!/^[a-zA-Z0-9._~!$&'()*+,;=:@%/-]+$/.test(id) || /%(?![0-9a-f]{2})/i.test(id)) return undefined
    try { if (decodeURIComponent(id).split('/').some((part) => !part || part === '.' || part === '..')) return undefined } catch { return undefined }
    return `https://media.discordapp.net/${id}`
  }
  const app = token(applicationId, 20)
  return app && /^\d{17,20}$/.test(app) && /^[a-zA-Z0-9_-]{1,128}$/.test(value) ? `https://cdn.discordapp.com/app-assets/${app}/${value}.png` : undefined
}
const activityImage = (activity: Record<string, unknown>) => {
  if (typeof activity.assets !== 'object' || activity.assets === null) return {}
  const assets = activity.assets as Record<string, unknown>
  const large = assetUrl(assets.large_image, activity.application_id)
  if (large) return { imageUrl: large }
  const small = assetUrl(assets.small_image, activity.application_id)
  return small ? { imageUrl: small } : {}
}
const parseActivity = (value: unknown): DiscordActivity | undefined => {
  if (typeof value !== 'object' || value === null) return undefined
  const activity = value as Record<string, unknown>; const name = text(activity.name); const type = activity.type
  if (!name || name === 'Spotify' || typeof type !== 'number' || !activityTypes.has(type)) return undefined
  const details = text(activity.details); const state = text(activity.state)
  return { type: type as DiscordActivity['type'], name, ...(details ? { details } : {}), ...(state ? { state } : {}), ...activityImage(activity) }
}

export const parseDiscordPresence = (payload: unknown): DiscordPresence | undefined => {
  if (typeof payload !== 'object' || payload === null) return undefined
  const response = payload as { success?: unknown; data?: unknown }
  if (response.success !== true || typeof response.data !== 'object' || response.data === null) return undefined
  const data = response.data as Record<string, unknown>; const state = data.discord_status
  if (state !== 'online' && state !== 'idle' && state !== 'dnd' && state !== 'offline') return undefined
  const activities: DiscordActivity[] = []
  if (data.listening_to_spotify === true && typeof data.spotify === 'object' && data.spotify !== null) {
    const spotify = data.spotify as Record<string, unknown>; const song = text(spotify.song); const artist = text(spotify.artist)
    if (song || artist) {
      const imageUrl = spotifyAlbumArt(spotify.album_art_url)
      activities.push({ type: 2, name: 'Spotify', ...(song ? { details: song } : {}), ...(artist ? { state: artist } : {}), ...(imageUrl ? { imageUrl } : {}) })
    }
  }
  if (Array.isArray(data.activities)) for (const item of data.activities) {
    const activity = parseActivity(item)
    if (activity) activities.push(activity)
  }
  return { state, ...(activities.length ? { activities } : {}) }
}

export const discordPresence = async (signal?: AbortSignal): Promise<DiscordPresence> => {
  if (!/^\d{17,20}$/.test(userId ?? '')) return { state: 'not-configured' }
  try {
    const response = await fetch(`https://api.lanyard.rest/v1/users/${userId}`, { signal })
    const presence = parseDiscordPresence(await response.json())
    if (!response.ok || !presence) throw new Error('Invalid Lanyard payload')
    return presence
  } catch { return { state: 'unavailable' } }
}

export const discordActivityLabel = (activity: DiscordActivity) => {
  const prefix = ({ 0: 'Playing', 1: 'Streaming', 2: 'Listening', 3: 'Watching', 4: 'Custom', 5: 'Competing' } as const)[activity.type]
  const name = activity.type === 4 && activity.name === 'Custom Status' ? undefined : activity.name
  return [[prefix, name].filter(Boolean).join(': '), activity.details, activity.state].filter(Boolean).join(' — ')
}
export const discordLabel = (presence: DiscordPresence) => presence.state === 'not-configured' ? 'Discord: not configured' : presence.state === 'loading' ? 'Discord: connecting…' : presence.state === 'unavailable' ? 'Discord: unavailable' : [`Discord: ${presence.state}`, ...presence.activities?.map(discordActivityLabel) ?? []].join(' · ')
